import { EventEmitter, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AuthService } from '@auth0/auth0-angular'
import { QueryClient } from '@tanstack/angular-query-experimental'
import { firstValueFrom } from 'rxjs'
import { finalize } from 'rxjs/operators'

const LAST_USER_KEY = 'gooh-politis:last-user-sub'

@Injectable()
export class AuthenticationService {
  data: any = {}
  changes: EventEmitter<any> = new EventEmitter<any>()
  private _isAuthenticated = false
  private isLoggingOut = false

  constructor(
    private auth0: AuthService,
    private queryClient: QueryClient,
    private router: Router,
  ) {
    this.auth0.isAuthenticated$.subscribe((isAuth) => {
      if (this.isLoggingOut) return
      this._isAuthenticated = isAuth
    })

    this.auth0.user$.subscribe((user) => {
      if (this.isLoggingOut) return

      if (user) {
        if (this.handleUserChange(user.sub)) return
        this.data = user['http://app.carto.com/user_metadata'] || {}
      } else {
        this.data = {}
      }
      this.changes.emit()
    })
  }

  // Returns true if the current user differs from the previously-cached
  // one. In that case forceLogout() has already wiped state — callers
  // should bail out instead of trusting the still-emitted user.
  private handleUserChange(currentSub: string | undefined): boolean {
    if (!currentSub) return false
    const stored = localStorage.getItem(LAST_USER_KEY)
    if (stored && stored !== currentSub) {
      this.forceLogout()
      return true
    }
    if (!stored) {
      localStorage.setItem(LAST_USER_KEY, currentSub)
    }
    return false
  }

  // Wipes everything we cache locally about the current user; safe to
  // call from any logout path.
  private clearLocalSession() {
    this.queryClient.clear()
    localStorage.removeItem(LAST_USER_KEY)
    this.data = {}
    this._isAuthenticated = false
  }

  // Logs out at the IdP. The isLoggingOut flag short-circuits the
  // user$ / isAuthenticated$ subscriptions for the brief window where
  // Auth0 may re-emit the outgoing user before its internal state
  // settles, so we don't reapply stale identity / metadata.
  private auth0Logout() {
    this.isLoggingOut = true
    this.auth0
      .logout({ openUrl: false })
      .pipe(
        finalize(() => {
          this.isLoggingOut = false
        }),
      )
      .subscribe()
  }

  private forceLogout() {
    this.clearLocalSession()
    this.auth0Logout()
    this.router.navigateByUrl('/login')
    this.changes.emit()
  }

  isLoggedIn(): boolean {
    return this._isAuthenticated
  }

  login() {
    this.auth0
      .loginWithRedirect({
        authorizationParams: { prompt: 'login' },
      })
      .subscribe()
  }

  logout() {
    this.clearLocalSession()
    this.auth0Logout()
    this.changes.emit()
  }

  getUser() {
    return this.data
  }

  getAccessToken$() {
    return this.auth0.getAccessTokenSilently()
  }

  async getAccessToken(): Promise<string> {
    try {
      return await firstValueFrom(this.auth0.getAccessTokenSilently())
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching access token:', error)
      this.logout()
      throw error
    }
  }
}
